import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  createStyles,
  withStyles,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Typography,
  Divider,
  Tooltip,
  Grid,
} from "@material-ui/core";
import axios from "axios";
import moment from "moment";
import { URL } from "../../config/config";
import { Close, Edit, GetApp } from "@material-ui/icons";
import { buildCsv } from "../../helpers/reportHelper";
import DateFilter from "../../commons/DateFilter/DateFilter";

function PartnerFinance({
  classes,
  partner,
  updateAttribute,
  hasWriteAccess,
  showErrorMessage,
  showSuccessMessage,
  agent,
}) {
  const [limit] = useState(10);
  const [page, setPage] = useState(1);
  const [pagesCache, setPagesCache] = useState({});
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [hasMoreForCurrent, setHasMoreForCurrent] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [editForm, setEditForm] = useState({
    paymentReceived: false,
    financeRemark: "",
    invoiceNumber: "",
  });
  const [dates, setDates] = useState({
    startDate: null,
    endDate: null,
    focusedInput: null,
  });
  const [dialogError, setDialogError] = useState("");

  const headers = [
    "Date",
    "Time",
    "Action",
    "Agent",
    "Previous Central Balance",
    "Amount",
    "Message",
    "Reason",
    "Payment Mode",
    "Finance Agent",
    "Payment Received",
    "Finance Remark",
    "Invoice Number",
    "Order ID",
    "Invoice Link",
  ];

  const currentPageRows = useMemo(
    () => pagesCache[page] || [],
    [pagesCache, page]
  );

  const totalFetchedCount = useMemo(() => {
    return Object.values(pagesCache).reduce(
      (acc, rows) => acc + (rows?.length || 0),
      0
    );
  }, [pagesCache]);

  const paginationCount = useMemo(
    () => (hasMoreForCurrent ? totalFetchedCount + 10 : totalFetchedCount),
    [hasMoreForCurrent, totalFetchedCount]
  );

  const fetchRechargeLogs = async (targetPage) => {
    if (pagesCache[targetPage]) return;
    try {
      setLoading(true);
      const response = await axios.post(`${URL}/superadmin/cb-recharge-log`, {
        partnerId: partner._id,
        limit,
        skip: (targetPage - 1) * limit,
        allActions: true,
      });
      const rows = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];
      setPagesCache((prev) => ({ ...prev, [targetPage]: rows }));
      if (rows.length < limit) {
        setHasMoreForCurrent(false);
      }
      setLoading(false);
    } catch (e) {
      console.error(e);
      showErrorMessage("Error while fetching recharge logs");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRechargeLogs(1);
  }, []);

  const handlePageChange = (event, newPageZeroBased) => {
    const newPage = newPageZeroBased + 1;
    setPage(newPage);
    if (!pagesCache[newPage]) {
      fetchRechargeLogs(newPage);
    }
  };

  const downloadReport = async () => {
    try {
      setDownloading(true);
      const payload = {
        partnerId: partner._id,
        allActions: true,
      };
      if (dates.startDate) payload.startDate = dates.startDate;
      if (dates.endDate) payload.endDate = dates.endDate;
      const response = await axios.post(
        `${URL}/superadmin/cb-recharge-log`,
        payload
      );
      const rows = Array.isArray(response?.data)
        ? response.data
        : response?.data?.data || [];
      const csv = buildCsv(rows, headers);
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const fileStart = dates.startDate
        ? new Date(dates.startDate).toLocaleDateString()
        : "all";
      const fileEnd = dates.endDate
        ? new Date(dates.endDate).toLocaleDateString()
        : "all";
      link.href = url;
      link.download = `${partner?.brandName}-CB-recharge-${fileStart}-to-${fileEnd}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      showErrorMessage("Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const openEdit = (log) => {
    setEditingLog(log);
    setEditForm({
      paymentReceived: !!log.paymentReceived,
      financeRemark: log.financeRemark || "",
      invoiceNumber: log.invoiceNumber || "",
    });
    setEditOpen(true);
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingLog(null);
    setDialogError("");
  };

  const handleEditChange = (field) => (e) => {
    const value =
      field === "paymentReceived" ? e.target.checked : e.target.value;
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveEdit = async () => {
    if (!editingLog) return;
    if (
      (editingLog?.paymentReceived || false) === editForm.paymentReceived &&
      (editingLog?.financeRemark?.trim() || "") ===
        editForm.financeRemark.trim() &&
      (editingLog?.invoiceNumber?.trim() || "") ===
        editForm.invoiceNumber.trim()
    ) {
      setDialogError("Please edit the fields to update the log");
      return;
    }
    try {
      const updateObj = {
        paymentReceived: !!editForm.paymentReceived,
        financeRemark: editForm.financeRemark,
        invoiceNumber: editForm.invoiceNumber,
        financeAgent: {
          id: agent.id,
          email: agent.email,
        },
      };
      await axios.post(`${URL}/superadmin/update-partner-billing-log`, {
        logId: editingLog._id,
        ...updateObj,
      });
      const updated = {
        ...editingLog,
        ...updateObj,
      };

      setPagesCache((prev) => {
        const next = { ...prev };
        const pageNumbers = Object.keys(next);
        for (let i = 0; i < pageNumbers.length; i += 1) {
          const pNum = Number(pageNumbers[i]);
          const list = next[pNum] || [];
          const idx = list.findIndex((r) => r._id === updated._id);
          if (idx !== -1) {
            const newList = [...list];
            newList[idx] = updated;
            next[pNum] = newList;
            break;
          }
        }
        return next;
      });
      closeEdit();
      showSuccessMessage("Billing log updated successfully");
    } catch (err) {
      console.error(err);
      showErrorMessage("Failed to update log");
    }
    setDialogError("");
  };

  console.log("here", dates);

  const handleTdsToggle = async () => {
    await updateAttribute("chargeTds", !(partner?.chargeTds === true));
  };

  return (
    <>
      <Box className={classes.root}>
        <Box py={2} px={2} my={2} className={classes.container}>
          <Typography
            variant="body1"
            gutterBottom
            style={{ fontWeight: "bold" }}
          >
            Charge TDS (2% Tax Deducted at Source)
          </Typography>
          <Box style={{ display: "flex", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  name="chargeTds"
                  checked={partner?.chargeTds === true}
                  onChange={handleTdsToggle}
                  disabled={!hasWriteAccess}
                  color="primary"
                />
              }
              label={partner?.chargeTds === true ? "Enabled" : "Disabled"}
            />
          </Box>
        </Box>

        <Box py={2} px={2} my={2} className={classes.container}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" gridGap={12} alignItems="center">
              <DateFilter
                filterName="Parter Report Date range"
                filterKey="PartnerReport"
                dates={dates}
                setFilter={setDates}
              />
            </Box>
            <Box>
              <Button
                color="primary"
                variant="contained"
                onClick={downloadReport}
                disabled={
                  downloading ||
                  !hasWriteAccess ||
                  !dates.startDate ||
                  !dates.endDate
                }
              >
                {downloading ? "Preparing..." : "Download Report"}
              </Button>
            </Box>
          </Box>
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table
              size="small"
              style={{
                tableLayout: "auto",
                minWidth: "max-content",
                overflowX: "auto",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Date</TableCell>
                  <TableCell className={classes.tableHeadCell}>Time</TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Action
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">
                    Agent
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">
                    Previous Central Balance
                  </TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">
                    Amount
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Message
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Reason
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Payment Mode
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Finance Agent
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Payment Received
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Finance Remark
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Invoice Number
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Order ID
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Invoice
                  </TableCell>
                  <TableCell className={classes.tableHeadCell}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPageRows.map((row, index) => {
                  const amount = (row.amount || 0) / 100000;
                  const displayDate = row.createdAt
                    ? moment(row.createdAt).format("DD/MM/YYYY")
                    : "";
                  const displaTime = row.createdAt
                    ? moment(row.createdAt).format("h:mm a")
                    : "";
                  const paymentYesNo = row.paymentReceived ? "Yes" : "No";
                  return (
                    <TableRow key={`${row._id || index}`}>
                      <TableCell className={classes.tableCellNowrap}>
                        {displayDate}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {displaTime}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {row.action || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {row?.superadminAgent?.email || "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classes.tableCellNowrap}
                      >
                        {row.prevCentralBalance != null
                          ? parseFloat((row.prevCentralBalance / 100000).toFixed(5))
                          : "-"}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={classes.tableCellNowrap}
                      >
                        {amount.toFixed(2)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {row?.message ? (
                          <Tooltip
                            title={
                              <div style={{ whiteSpace: "pre-wrap" }}>
                                {row.message}
                              </div>
                            }
                            arrow
                            placement="top"
                            interactive
                            enterDelay={200}
                            leaveDelay={200}
                          >
                            <span>
                              {row.message?.length > 30
                                ? row.message.slice(0, 30) + "…"
                                : row?.message}
                            </span>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {row.reasonCode || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {row.balanceType || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {row?.financeAgent?.email || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {paymentYesNo}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {row?.financeRemark ? (
                          <Tooltip
                            title={
                              <div style={{ whiteSpace: "pre-wrap" }}>
                                {row.financeRemark}
                              </div>
                            }
                            arrow
                            placement="top"
                            interactive
                            enterDelay={200}
                            leaveDelay={200}
                          >
                            <span>
                              {row.financeRemark?.length > 30
                                ? row.financeRemark.slice(0, 30) + "…"
                                : row?.financeRemark}
                            </span>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {row.invoiceNumber || "-"}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {row.orderId ? (
                          <Tooltip
                            title={row.orderId}
                            arrow
                            placement="top"
                            interactive
                          >
                            <span>
                              {row.orderId.length > 20
                                ? row.orderId.slice(0, 20) + "…"
                                : row.orderId}
                            </span>
                          </Tooltip>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        {row.invoiceLink ? (
                          <IconButton
                            size="small"
                            color="primary"
                            href={row.invoiceLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            component="a"
                          >
                            <GetApp />
                          </IconButton>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCellNowrap}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEdit(row)}
                          disabled={!hasWriteAccess}
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={16}>
                      <Box display="flex" justifyContent="center" py={24}>
                        <CircularProgress size={20} />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            display="flex"
            justifyContent="center"
            mt={1}
            className={classes.pagination}
          >
            <TablePagination
              component="div"
              count={paginationCount}
              page={page - 1 || 0}
              onPageChange={handlePageChange}
              rowsPerPage={limit}
              rowsPerPageOptions={[limit]}
            />
          </Box>
        </Box>
      </Box>

      <Dialog open={editOpen} onClose={closeEdit} fullWidth maxWidth="sm">
        <DialogTitle style={{ padding: "8px 24px" }}>
          <Box className={classes.dialogHeader}>
            <Typography variant="h3">Edit Recharge Log</Typography>
            <IconButton onClick={closeEdit} style={{ margin: 0 }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box display="flex" flexDirection="column" gridGap={16}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={!!editForm.paymentReceived}
                  onChange={handleEditChange("paymentReceived")}
                  name="paymentReceived"
                />
              }
              label="Payment Received"
            />
            <Typography variant="h4">Finance Remark</Typography>
            <textarea
              value={editForm.financeRemark}
              onChange={handleEditChange("financeRemark")}
              className={classes.textArea}
              maxLength={1024}
            />
            <Typography variant="h4">Invoice Number</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={editForm.invoiceNumber}
              onChange={handleEditChange("invoiceNumber")}
              maxLength={1024}
            />
          </Box>
          <Box className={classes.dialogErrorMsg}>
            <Typography variant="caption">{dialogError}</Typography>
          </Box>
        </DialogContent>
        <Box my={1} />
        <Divider />
        <DialogActions style={{ paddingRight: "24px", margin: "4px 0px" }}>
          <Button onClick={closeEdit}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={saveEdit}
            disabled={!hasWriteAccess}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const styles = (theme) =>
  createStyles({
    root: {
      width: "100%",
      boxSizing: "border-box",
      overflowX: "hidden",
      position: "relative",
    },
    container: {
      background: "#EBF5F3",
      borderRadius: "15px",
    },
    tableContainer: {
      width: "100%",
      maxWidth: 1050,
      overflowX: "auto",
      height: 480,
    },
    tableHeadCell: {
      whiteSpace: "nowrap",
      fontWeight: 600,
      textAlign: "center",
      fontSize: 14,
      padding: "12px 10px",
    },
    tableCellNowrap: {
      whiteSpace: "nowrap",
      textAlign: "center",
      fontSize: 14,
    },
    tableCell: {
      whiteSpace: "nowrap",
      textAlign: "center",
      fontSize: 14,
    },
    textArea: {
      borderRadius: "8px",
      borderColor: "transparent",
      background: "#f0F0F0",
      height: "90px",
      padding: "18px 24px",
      "&:focus": {
        border: "none",
        outline: "none",
      },
    },
    dialogHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    dialogErrorMsg: {
      color: "red",
      marginTop: "8px",
    },

    pagination: {
      "& .MuiTablePagination-toolbar": {
        paddingLeft: 0,
      },
    },
  });

export default withStyles(styles)(PartnerFinance);
